import { useEffect, useRef, useState, useCallback } from 'react';
import { X, Radio, MonitorOff } from 'lucide-react';
import { API_URL } from '@/services/apiClient';
import { io, type Socket } from 'socket.io-client';

interface StudentLiveViewProps {
  studentId: string;
  studentName: string;
  dm: boolean;
  onClose: () => void;
}

const CONNECTION_TIMEOUT_MS = 10_000; // 10 seconds before showing "not reachable"

export function StudentLiveView({ studentId, studentName, dm, onClose }: StudentLiveViewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const frameCountRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [windowTitle, setWindowTitle] = useState('');
  const [fps, setFps] = useState(0);
  const [lastFrameTime, setLastFrameTime] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Start REST call to trigger the student's Electron app
  const startSession = useCallback(async () => {
    setError(null);
    const token = sessionStorage.getItem('authToken');
    try {
      const res = await fetch(
        `${API_URL}/dashboard/school-admin/students/${studentId}/live-view/start`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        // error handler returns { error: { code, message } } or { error: "string" }
        const err = body.error;
        const msg = typeof err === 'string' ? err : err?.message || 'Failed to start live view';
        setError(msg);
        return false;
      }
      return true;
    } catch {
      setError('Network error starting live view');
      return false;
    }
  }, [studentId]);

  const stopSession = useCallback(async () => {
    const token = sessionStorage.getItem('authToken');
    try {
      await fetch(
        `${API_URL}/dashboard/school-admin/students/${studentId}/live-view/stop`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
    } catch { /* best-effort */ }
  }, [studentId]);

  // Socket connection + frame listener
  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (!token) { setError('Not authenticated'); return; }

    const socket = io(API_URL.replace('/api', ''), { auth: { token } });
    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      // Join the live view room for this student
      socket.emit('start_live_view', { studentId });
    });

    socket.on('connect_error', () => {
      setError('Socket connection failed');
    });

    // Incoming frames from the student's Electron app
    socket.on('live_frame', (data: {
      studentId: string;
      frame: string;
      timestamp: number;
      windowTitle?: string;
    }) => {
      if (data.studentId !== studentId) return;

      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
      };
      img.src = `data:image/jpeg;base64,${data.frame}`;

      // First frame received — clear the timeout
      if (!isStreaming && timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      setIsStreaming(true);
      setLastFrameTime(data.timestamp);
      setWindowTitle(data.windowTitle || '');
      frameCountRef.current++;
    });

    // Student went offline or admin was disconnected
    socket.on('live_view_ended', (data: { studentId: string; reason: string }) => {
      if (data.studentId !== studentId) return;
      setIsStreaming(false);
      setError('Student disconnected');
    });

    return () => {
      socket.emit('stop_live_view', { studentId });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [studentId]);

  // Fire the REST call + start a connection timeout
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const ok = await startSession();
      if (!ok || cancelled) return;

      // If no frames arrive within the timeout, show a helpful error
      timeoutRef.current = setTimeout(() => {
        if (!cancelled) {
          setError(
            'No response from student. Make sure the student is online and using the ShieldED Haven desktop app.',
          );
        }
      }, CONNECTION_TIMEOUT_MS);
    })();

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      stopSession();
    };
  }, [startSession, stopSession]);

  // FPS counter — updates every second
  useEffect(() => {
    const id = setInterval(() => {
      setFps(frameCountRef.current);
      frameCountRef.current = 0;
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const timeSinceFrame = lastFrameTime
    ? `${Math.round((Date.now() - lastFrameTime) / 1000)}s ago`
    : null;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-black">
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-2.5 shrink-0 ${dm ? 'bg-slate-900' : 'bg-slate-800'} text-white`}>
        <div className="flex items-center gap-4">
          <span className="font-semibold text-sm">{studentName}</span>

          {windowTitle && (
            <span className="text-xs text-slate-400 truncate max-w-[300px]">{windowTitle}</span>
          )}

          {isStreaming && (
            <span className="flex items-center gap-1.5 text-xs text-red-400">
              <Radio className="h-3 w-3 animate-pulse" />
              LIVE &mdash; {fps} fps
            </span>
          )}

          {isConnected && !isStreaming && !error && (
            <span className="text-xs text-amber-400">Waiting for student frames...</span>
          )}
        </div>

        <button
          onClick={onClose}
          className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-red-600 hover:bg-red-700 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
          End Live View
        </button>
      </div>

      {/* Canvas area */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        {error ? (
          <div className="flex flex-col items-center gap-3 text-slate-400 max-w-md text-center px-4">
            <MonitorOff className="h-16 w-16 opacity-40" />
            <p className="text-sm">{error}</p>
            <div className="flex items-center gap-2 mt-1">
              <button
                onClick={() => {
                  setError(null);
                  setIsStreaming(false);
                  startSession().then(ok => {
                    if (ok) {
                      timeoutRef.current = setTimeout(() => {
                        setError(
                          'No response from student. Make sure the student is online and using the ShieldED Haven desktop app.',
                        );
                      }, CONNECTION_TIMEOUT_MS);
                    }
                  });
                }}
                className="px-4 py-1.5 rounded-lg text-xs bg-slate-700 hover:bg-slate-600 text-white transition-colors"
              >
                Retry
              </button>
              <button
                onClick={onClose}
                className="px-4 py-1.5 rounded-lg text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : !isStreaming ? (
          <div className="flex flex-col items-center gap-3 text-slate-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            <p className="text-sm">Connecting to student screen...</p>
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        )}
      </div>

      {/* Footer status bar */}
      {isStreaming && (
        <div className={`flex items-center justify-between px-4 py-1.5 text-xs shrink-0 ${dm ? 'bg-slate-900 text-slate-500' : 'bg-slate-800 text-slate-400'}`}>
          <span>960 x 540 &middot; JPEG stream</span>
          {timeSinceFrame && <span>Last frame: {timeSinceFrame}</span>}
        </div>
      )}
    </div>
  );
}
